import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';

// Hook pour les médecins - Version simplifiée
export const useDoctors = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (hasLoaded) return;
    
    const fetchDoctors = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiService.getDoctors();
        
        if (result.data && result.data.doctors) {
          setData(result.data.doctors);
        } else if (result.data && Array.isArray(result.data)) {
          setData(result.data);
        } else {
          setData([]);
        }
        
        setHasLoaded(true);
      } catch (err) {
        setError(err.message);
        console.error('Erreur médecins:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, [hasLoaded]);

  const refetch = useCallback(async () => {
    setHasLoaded(false);
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getDoctors();
      
      if (result.data && result.data.doctors) {
        setData(result.data.doctors);
      } else if (result.data && Array.isArray(result.data)) {
        setData(result.data);
      } else {
        setData([]);
      }
      setHasLoaded(true);
    } catch (err) {
      setError(err.message);
      console.error('Erreur médecins:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { 
    data, 
    loading, 
    error, 
    refetch 
  };
};

// Hook pour les patients - Version ultra-simplifiée
export const usePatients = (doctorId = null) => {
  // Initialiser avec un tableau vide et loading true pour éviter l'affichage de données
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastDoctorId, setLastDoctorId] = useState(Symbol('INITIAL')); // Utiliser un Symbol pour l'état initial

  useEffect(() => {
    // Si doctorId est undefined, ne pas charger (on attend qu'il soit défini)
    // Vider les données immédiatement pour éviter l'affichage
    if (doctorId === undefined) {
      setLoading(true);
      setData([]);
      setError(null);
      return;
    }

    // Éviter les appels multiples avec le même doctorId
    if (lastDoctorId === doctorId) {
      return;
    }

    const fetchPatients = async () => {
      try {
        setLoading(true);
        setError(null);
        // Vider les données avant de charger pour éviter l'affichage d'anciennes données
        setData([]);
        
        // Si un doctorId est fourni, utiliser l'endpoint dédié
        let result;
        if (doctorId !== null && doctorId !== undefined) {
          // doctorId est un nombre, charger les patients du docteur
          console.log('📞 Appel API getPatientsByDoctor avec doctorId:', doctorId);
          result = await apiService.getPatientsByDoctor(doctorId);
          console.log('📥 Réponse getPatientsByDoctor:', result);
        } else {
          // doctorId est null, charger tous les patients (pour les admins uniquement)
          console.log('📞 Appel API getPatients (tous les patients)');
          result = await apiService.getPatients();
          console.log('📥 Réponse getPatients:', result);
        }
        
        if (result.data && Array.isArray(result.data)) {
          console.log('✅ Patients chargés:', result.data.length);
          setData(result.data);
        } else {
          console.warn('⚠️ Format de réponse inattendu:', result);
          setData([]);
        }
        
        setLastDoctorId(doctorId);
      } catch (err) {
        setError(err.message);
        console.error('❌ Erreur patients:', err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPatients();
  }, [doctorId, lastDoctorId]);

  const refetch = useCallback(async () => {
    // Si doctorId est undefined, ne pas refetch (on attend qu'il soit défini)
    if (doctorId === undefined) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Si un doctorId est fourni, utiliser l'endpoint dédié
      let result;
      if (doctorId !== null && doctorId !== undefined) {
        // doctorId est un nombre, charger les patients du docteur
        result = await apiService.getPatientsByDoctor(doctorId);
      } else {
        // doctorId est null, charger tous les patients (pour les admins uniquement)
        result = await apiService.getPatients();
      }
      
      if (result.data && Array.isArray(result.data)) {
        setData(result.data);
      } else {
        setData([]);
      }
      setLastDoctorId(doctorId);
    } catch (err) {
      setError(err.message);
      console.error('Erreur patients:', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [doctorId]);

  return { 
    data, 
    loading, 
    error, 
    refetch 
  };
};

// Hook pour les maladies - Version simplifiée
export const useDiseases = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (hasLoaded) return;
    
    const fetchDiseases = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiService.getDiseases();
        
        if (result.data && Array.isArray(result.data)) {
          setData(result.data);
        } else {
          setData([]);
        }
        
        setHasLoaded(true);
      } catch (err) {
        setError(err.message);
        console.error('Erreur maladies:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDiseases();
  }, [hasLoaded]);

  const refetch = useCallback(async () => {
    setHasLoaded(false);
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getDiseases();
      
      if (result.data && Array.isArray(result.data)) {
        setData(result.data);
      } else {
        setData([]);
      }
      setHasLoaded(true);
    } catch (err) {
      setError(err.message);
      console.error('Erreur maladies:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { 
    data, 
    loading, 
    error, 
    refetch 
  };
};

// Hook pour les études - Version simplifiée
export const useStudies = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (hasLoaded) return;
    
    const fetchStudies = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiService.getStudies();
        
        if (result.data && Array.isArray(result.data)) {
          setData(result.data);
        } else {
          setData([]);
        }
        
        setHasLoaded(true);
      } catch (err) {
        setError(err.message);
        console.error('Erreur études:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStudies();
  }, [hasLoaded]);

  const refetch = useCallback(async () => {
    setHasLoaded(false);
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getStudies();
      
      if (result.data && Array.isArray(result.data)) {
        setData(result.data);
      } else {
        setData([]);
      }
      setHasLoaded(true);
    } catch (err) {
      setError(err.message);
      console.error('Erreur études:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { 
    data, 
    loading, 
    error, 
    refetch 
  };
};

// Hook pour les zones de santé - Version simplifiée
export const useHealthZones = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (hasLoaded) return;
    
    const fetchHealthZones = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiService.getHealthZones();
        
        if (result.data && Array.isArray(result.data)) {
          setData(result.data);
        } else {
          setData([]);
        }
        
        setHasLoaded(true);
      } catch (err) {
        setError(err.message);
        console.error('Erreur zones de santé:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHealthZones();
  }, [hasLoaded]);

  const refetch = useCallback(async () => {
    setHasLoaded(false);
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getHealthZones();
      
      if (result.data && Array.isArray(result.data)) {
        setData(result.data);
      } else {
        setData([]);
      }
      setHasLoaded(true);
    } catch (err) {
      setError(err.message);
      console.error('Erreur zones de santé:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { 
    data, 
    loading, 
    error, 
    refetch 
  };
};

// Hook pour les hôpitaux - Version simplifiée
export const useHospitals = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (hasLoaded) return;
    
    const fetchHospitals = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiService.getHospitals();
        
        if (result.data && Array.isArray(result.data)) {
          setData(result.data);
        } else {
          setData([]);
        }
        
        setHasLoaded(true);
      } catch (err) {
        setError(err.message);
        console.error('Erreur hôpitaux:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHospitals();
  }, [hasLoaded]);

  const refetch = useCallback(async () => {
    setHasLoaded(false);
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getHospitals();
      
      if (result.data && Array.isArray(result.data)) {
        setData(result.data);
      } else {
        setData([]);
      }
      setHasLoaded(true);
    } catch (err) {
      setError(err.message);
      console.error('Erreur hôpitaux:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { 
    data, 
    loading, 
    error, 
    refetch 
  };
};

// Hook pour les médicaments
export const useMedicaments = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (hasLoaded) return;
    
    const fetchMedicaments = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiService.getMedicaments();
        
        if (result.data && Array.isArray(result.data)) {
          setData(result.data);
        } else {
          setData([]);
        }
        
        setHasLoaded(true);
      } catch (err) {
        setError(err.message);
        console.error('Erreur médicaments:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMedicaments();
  }, [hasLoaded]);

  const refetch = useCallback(async () => {
    setHasLoaded(false);
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getMedicaments();
      
      if (result.data && Array.isArray(result.data)) {
        setData(result.data);
      } else {
        setData([]);
      }
      setHasLoaded(true);
    } catch (err) {
      setError(err.message);
      console.error('Erreur médicaments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { 
    data, 
    loading, 
    error, 
    refetch 
  };
};

// Hook pour les consultations
export const useConsultations = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (hasLoaded) return;
    
    const fetchConsultations = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await apiService.getConsultations();
        
        if (result.data && Array.isArray(result.data)) {
          setData(result.data);
        } else {
          setData([]);
        }
        
        setHasLoaded(true);
      } catch (err) {
        setError(err.message);
        console.error('Erreur consultations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConsultations();
  }, [hasLoaded]);

  const refetch = useCallback(async () => {
    setHasLoaded(false);
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getConsultations();
      
      if (result.data && Array.isArray(result.data)) {
        setData(result.data);
      } else {
        setData([]);
      }
      setHasLoaded(true);
    } catch (err) {
      setError(err.message);
      console.error('Erreur consultations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { 
    data, 
    loading, 
    error, 
    refetch 
  };
};

// Hook pour les opérations CRUD
export const useCrud = (apiService, entityName) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const create = async (data) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.create(data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const update = async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.update(id, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.delete(id);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    create,
    update,
    remove,
    loading,
    error
  };
};
